import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CassError } from '@core/models/cass-error';
import { Message } from '@core/models/message.model';
import { Publisher } from '@core/models/product.model';
import { UserProfile } from '@core/models/user-profile';
import { MessageService } from '@core/services/message.service';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { ChatConversationsListComponent } from '@messages/chat-conversations-list/chat-conversations-list.component';
import { UserAlertService } from '@shared/services/user-alert.service';
import { BehaviorSubject, map } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-messages-page',
	templateUrl: './messages-page.component.html',
	styleUrls: ['./messages-page.component.css']
})
export class MessagesPageComponent implements OnInit, OnDestroy {

	@ViewChild(ChatConversationsListComponent) conversationsListComponent: ChatConversationsListComponent | undefined;

	updatingMessages$ = new BehaviorSubject<boolean>(false);

	selectedMessageId: string | undefined;

	orderId?: string;
	chatMessages: Message[] = [];
	currentUserId?: string;
	publisher: Publisher | undefined;
	scrollToMessageId: string | undefined;

	private subs = new SubSink();

	constructor (
		private _userProfileService: UserProfilesService,
		private _messageService: MessageService,
		private _alertService: UserAlertService) {
	}

	ngOnInit(): void {
		this.subs.add(
			this._userProfileService.userProfile$.subscribe((profile: UserProfile | undefined) => {
				if (profile) {
					this.currentUserId = profile.id;
				}
			}));
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}

	conversationSelected(arg: { message: Message, publisher: Publisher | undefined }): void {

		this.updatingMessages$.next(true);

		if (arg.message && arg.message.orderId && (arg.message.senderCompanyId || arg.message.toCompanyId)) {
			this._messageService.getConversationsForOrderId(arg.message.orderId)
				.pipe(
					map((conversationMap: Map<string, Message[]>) => {
						const publisherId = arg.message.senderCompanyId ?? arg.message.toCompanyId; // may be empty
						return conversationMap.get(publisherId || "") || [];
					})
				)
				.subscribe({
					next: (messages: Message[]) => {
						this.updatingMessages$.next(false);

						this.markConversationAsSelected(arg.message);
						arg.message.isRead = true;

						this.chatMessages = messages;
						this.orderId = arg.message.orderId;
						this.publisher = arg.publisher;
					},
					error: (error: CassError) => {
						this.updatingMessages$.next(false);
						this._alertService.errorByCassError(error);
					}
				});
		}
	}

	private markConversationAsSelected(message: Message): void {
		this.selectedMessageId = message && message.id || undefined;
	}

	chatMessageSent(messageId: string) {
		this.conversationsListComponent?.updateConversations(); // async
		this.selectedMessageId = messageId;
	}
}
